import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'
import { useProductStore } from '@/js/stores/productStore'
import type { CustomProductVariant, CustomProductId } from './types'
import { caveVariant } from './variants/cave'
import { hybridRigVariant } from './variants/hybridRig'
import { massStorageVariant } from './variants/massStorage'
import { monsterMassStorageVariant } from './variants/monsterMassStorage'
import { slingerXVariant } from './variants/slingerX'

export const CUSTOM_PRODUCT_REGISTRY: readonly CustomProductVariant[] = [
  caveVariant,
  hybridRigVariant,
  massStorageVariant,
  monsterMassStorageVariant,
  slingerXVariant,
]

function isFlagEnabled(rogue: IRogue, variant: CustomProductVariant): boolean {
  return !variant.featureFlag || !!rogue.featureFlags?.isOn(variant.featureFlag)
}

// Unflagged — cart/list membership must not change when a flag is toggled; a row is excluded unless its variant readmits it (MS/MMS bolt-kit lines carrying metadata.hardwareCount).
export function isCustomProductQtyExcluded(item: BuyRequest, sku: string | undefined): boolean {
  if (!sku) return false

  return CUSTOM_PRODUCT_REGISTRY.some(variant =>
    variant.skuSets.qtyExcludedSkus.includes(sku) && !variant.readmitFromQty?.(item)
  )
}

// Unflagged — mirrors isCustomProductQtyExcluded but against the add-to-cart message count's own (purpose-keyed) SKU set.
export function isCustomProductAddCountExcluded(sku: string | undefined): boolean {
  if (!sku) return false

  return CUSTOM_PRODUCT_REGISTRY.some(variant => variant.skuSets.addCountExcludedSkus.includes(sku))
}

// Flag-gated membership check against one variant's purpose-keyed SKU set.
export function isEnabledCustomProductMember(
  rogue: IRogue,
  id: CustomProductId,
  setKey: keyof CustomProductVariant['skuSets'],
  sku: string | undefined
): boolean {
  if (!sku) return false

  const variant = CUSTOM_PRODUCT_REGISTRY.find(v => v.id === id)

  return !!variant && isFlagEnabled(rogue, variant) && variant.skuSets[setKey].includes(sku)
}

// Flag-gated: is this sku the hero/main line of any enabled custom product (cart/SFL's "render as the custom bundle row" gate)?
export function isEnabledCustomProductMainSku(rogue: IRogue, sku: string | undefined): boolean {
  if (!sku) return false

  return CUSTOM_PRODUCT_REGISTRY.some(variant => isFlagEnabled(rogue, variant) && variant.skuSets.mainSkus.includes(sku))
}

// Delegates to a variant's own `matches` predicate rather than shipping a second resolver beside it (e.g. caveUtils.ts's isTheCave).
export function isEnabledCustomProduct(
  rogue: IRogue,
  id: CustomProductId,
  product: Product | undefined,
  groupProduct?: Product | undefined
): boolean {
  const variant = CUSTOM_PRODUCT_REGISTRY.find(v => v.id === id)

  return !!variant && isFlagEnabled(rogue, variant) && variant.matches(product, groupProduct)
}

// Unflagged — generalizes call sites (listStore's qty cascade, group remove/move) that fired regardless of flag state before this registry existed.
export function findCustomProductVariantByMainSku(sku: string | undefined): CustomProductVariant | undefined {
  if (!sku) return undefined

  return CUSTOM_PRODUCT_REGISTRY.find(variant => variant.skuSets.mainSkus.includes(sku))
}

// Flag-gated, any member sku: order history prices a purchase from whichever line stands in as its row, which for a Slinger X add-on is a side bundle rather than a main sku.
export function findEnabledCustomProductVariantBySku(rogue: IRogue, sku: string | undefined): CustomProductVariant | undefined {
  if (!sku) return undefined

  return CUSTOM_PRODUCT_REGISTRY.find(variant => isFlagEnabled(rogue, variant) && variant.skuSets.allSkus.includes(sku))
}

// Catalog sku first - a persisted line sku can be a per-configuration code (Slinger X bundles) - falling back to it for lines whose product no longer loads (order history).
export function lineSku(rogue: IRogue, item: BuyRequest): string | undefined {
  return useProductStore(rogue.pinia).products[item.productId]?.sku || item.sku
}

// Flag-gated like the PDP routing that rendered the custom RHPA in the first place; a plain bundle or a flagged-off product passes through untouched.
export function prepareCustomProductForCart(rogue: IRogue, buyRequest: BuyRequest): void {
  findEnabledCustomProductVariantBySku(rogue, lineSku(rogue, buyRequest))?.prepareForCart?.(buyRequest)
}

// Unflagged for the same reason as findCustomProductVariantByMainSku and for the same call sites (qty cascade, group remove/move, the rows a lead line owns in cart and order history). Ordered by unit id so callers that label siblings ("Unit 2") read them in the order they were added.
export function findCustomProductSiblings(rogue: IRogue, lead: BuyRequest, listItems: BuyRequest[]): BuyRequest[] {
  const variant = findCustomProductVariantByMainSku(lineSku(rogue, lead))
  const leadKey = variant?.purchaseKey(lead)

  if (!variant || !leadKey) return []

  const leadId = lead._id || lead.listItemId

  return listItems
    .filter(item => item !== lead && (!leadId || (item._id || item.listItemId) !== leadId))
    .filter(item => variant.purchaseKey(item) === leadKey)
    .filter(item => {
      const sku = lineSku(rogue, item)

      return !!sku && variant.skuSets.itemSkus.includes(sku)
    })
    .sort((itemA, itemB) => (itemA.metadata?.id || 0) - (itemB.metadata?.id || 0))
}
